import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Fellowship.css';

// Assets
import darkLogo from '../Assets/Images/Dark_Logo_Transparent.png';

const Fellowship = () => {
  const handleApplyClick = () => {
    window.location.href = 'mailto:andrew@privateiq.golf?subject=Summer Fellowship Application';
  };

  return (
    <div className="fellowship-page">
      <Helmet>
        <title>Summer Fellowship Program | PrivateIQ</title>
        <meta name="description" content="Join PrivateIQ's Summer Fellowship Program. Hands-on exposure to a real startup, real users, and real brand growth with mentorship and portfolio-ready work." />
      </Helmet>

      <header className="fellowship-header">
        <div className="fellowship-logo-row">
          <Link to="/" className="fellowship-logo">
            <img src={darkLogo} alt="PrivateIQ" className="fellowship-logo-img" />
          </Link>
          <div className="fellowship-badge">Summer 2026</div>
        </div>
        <div className="fellowship-headline">
          <h1>Summer <em>Fellowship</em><br />Program</h1>
          <p className="fellowship-tagline">Hands-on exposure to a real startup, real users, and real brand growth — with mentorship and portfolio-ready work.</p>
        </div>
        <div className="fellowship-meta-strip">
          <div className="fellowship-meta-item">
            <div className="fellowship-meta-icon">📅</div>
            <div className="fellowship-meta-text">
              <span className="fellowship-meta-label">Duration</span>
              <span className="fellowship-meta-value">8 Weeks (June–July)</span>
            </div>
          </div>
          <div className="fellowship-meta-item">
            <div className="fellowship-meta-icon">⏱️</div>
            <div className="fellowship-meta-text">
              <span className="fellowship-meta-label">Commitment</span>
              <span className="fellowship-meta-value">10–15 hrs/week</span>
            </div>
          </div>
          <div className="fellowship-meta-item">
            <div className="fellowship-meta-icon">🌐</div>
            <div className="fellowship-meta-text">
              <span className="fellowship-meta-label">Location</span>
              <span className="fellowship-meta-value">Fully Remote</span>
            </div>
          </div>
        </div>
      </header>

      <main className="fellowship-content">
        <section className="fellowship-intro">
          <div className="fellowship-intro-text">
            <h2>About PrivateIQ</h2>
            <p>PrivateIQ is a fast-growing sports technology startup building the leading digital platform for the country club industry. We work at the intersection of sports, data, design, and community — serving thousands of golfers across the U.S.</p>
            <p style={{ marginTop: '12px' }}>This fellowship is designed to be compatible with summer classes, part-time jobs, or athletics.</p>
          </div>
          <div className="fellowship-what-you-gain">
            <h3>What Fellows Gain</h3>
            <div className="fellowship-gain-list">
              <div className="fellowship-gain-item">
                <span className="fellowship-gain-check">✓</span>
                <span>Real-world startup experience with live products</span>
              </div>
              <div className="fellowship-gain-item">
                <span className="fellowship-gain-check">✓</span>
                <span>Portfolio-ready projects for your resume</span>
              </div>
              <div className="fellowship-gain-item">
                <span className="fellowship-gain-check">✓</span>
                <span>Direct mentorship from the founder & team</span>
              </div>
              <div className="fellowship-gain-item">
                <span className="fellowship-gain-check">✓</span>
                <span>Insight into sports business & digital growth</span>
              </div>
              <div className="fellowship-gain-item">
                <span className="fellowship-gain-check">✓</span>
                <span>Letter of recommendation upon completion</span>
              </div>
            </div>
          </div>
        </section>

        <section className="fellowship-tracks-section">
          <div className="fellowship-section-header">
            <h2>Fellowship Tracks</h2>
            <div className="fellowship-section-line"></div>
          </div>
          <div className="fellowship-tracks-grid">
            <div className="fellowship-track-card design">
              <div className="fellowship-track-emoji">🎨</div>
              <div className="fellowship-track-title">Design Fellow</div>
              <div className="fellowship-track-subtitle">Graphic / UI / Brand</div>
              <div className="fellowship-track-projects">Example Projects</div>
              <ul className="fellowship-track-list">
                <li>Social & digital graphics for sports media</li>
                <li>App UI mockups & feature concepts</li>
                <li>Visual brand systems & templates</li>
                <li>Portfolio-ready sports design work</li>
              </ul>
            </div>
            <div className="fellowship-track-card engineering">
              <div className="fellowship-track-emoji">💻</div>
              <div className="fellowship-track-title">Engineering Fellow</div>
              <div className="fellowship-track-subtitle">Software / Product</div>
              <div className="fellowship-track-projects">Example Projects</div>
              <ul className="fellowship-track-list">
                <li>Feature prototypes & internal tools</li>
                <li>Front-end / back-end experiments</li>
                <li>Real product workflows & code reviews</li>
                <li>Startup development cycle exposure</li>
              </ul>
            </div>
            <div className="fellowship-track-card social">
              <div className="fellowship-track-emoji">📱</div>
              <div className="fellowship-track-title">Social & Media Fellow</div>
              <div className="fellowship-track-subtitle">Sports Content / Growth</div>
              <div className="fellowship-track-projects">Example Projects</div>
              <ul className="fellowship-track-list">
                <li>Short-form sports content strategy</li>
                <li>Campaign performance analysis</li>
                <li>Audience & community research</li>
                <li>Content planning & brand storytelling</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="fellowship-bottom-grid">
          <div className="fellowship-who-section">
            <h3>Who Should Apply</h3>
            <ul className="fellowship-who-list">
              <li>Currently enrolled in college or university</li>
              <li>Interested in sports, startups, or technology</li>
              <li>Self-motivated and comfortable working remotely</li>
              <li>Looking for hands-on mentorship experience</li>
            </ul>
            <p className="fellowship-note">Prior professional experience is not required — curiosity, initiative, and a willingness to learn matter most.</p>
          </div>
          <div className="fellowship-apply-section">
            <h3>How to Apply</h3>
            <ul className="fellowship-apply-list">
              <li>
                <span className="fellowship-apply-num">1</span>
                <span>A short note about why you're interested in PrivateIQ and this fellowship</span>
              </li>
              <li>
                <span className="fellowship-apply-num">2</span>
                <span>Your resume</span>
              </li>
              <li>
                <span className="fellowship-apply-num">3</span>
                <span>Portfolio, GitHub, or work samples (optional but encouraged)</span>
              </li>
            </ul>
            <div className="fellowship-apply-cta">
              <button className="fellowship-cta-badge" onClick={handleApplyClick}>
                Apply Now
              </button>
              <span className="fellowship-rolling-text">Apply by February 28th, 2026</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="fellowship-footer">
        <Link to="/" className="fellowship-footer-logo">privateIQ</Link>
        <div className="fellowship-footer-tagline">Sports × Data × Design × Community</div>
      </footer>
    </div>
  );
};

export default Fellowship;
